'use client';

import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { OrderSide, OrderType } from '@polymarket/client';
import { useQueryClient } from '@tanstack/react-query';
import { encodeFunctionData, encodePacked, getAddress, isHex, keccak256, pad } from 'viem';

import { useAuth } from '@/hooks/useAuth';
import { useClobClient } from '@/hooks/useClobClient';
import { usePolymarketClient } from '@/hooks/usePolymarketClient';
import { ORDERS_QUERY_KEYS, PORTFOLIO_QUERY_KEYS, STREAK_QUERY_KEYS } from '@/services/query';
import { formatBuilderCode, normalizeClobAddress } from '@/utils/clob.utils';
import { executeRelayMetaTxBatch } from '@/utils/relayerBatch.utils';

import { POLY_BUILDER_CODE } from '@/constants/api.constants';
import { MESSAGES } from '@/constants/messages.constants';
import {
  CTF_COLLATERAL_ADAPTER_ADDRESS,
  CTF_CONTRACT_ADDRESS,
  NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS,
  PUSD_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens.constants';

export interface PlaceOrderParams {
  tokenId: string;
  amount: number; // For Market BUY: $$, for Limit or SELL: Shares
  price?: number;
  side: 'BUY' | 'SELL';
  userUSDCBalance?: number;
  expiration?: number;
  negRisk?: boolean;
}

/**
 * Helper to dynamically determine if a token ID belongs to pUSD collateral or legacy USDC.e collateral
 * using the standard Gnosis CTF derivation:
 * tokenId = uint256(keccak256(abi.encodePacked(collateralToken, keccak256(abi.encodePacked(parentCollectionId, conditionId, 1n << outcomeIndex)))))
 */
function isPusdCollateral(tokenId?: string, conditionId?: string, outcomeIndex?: number): boolean {
  if (!tokenId || !conditionId || outcomeIndex === undefined || outcomeIndex === null) {
    // Default to true (pUSD) as it is the standard V2 collateral
    return true;
  }

  try {
    const zeroBytes32 = '0x' + '0'.repeat(64);
    const checksummedPusd = getAddress(PUSD_CONTRACT_ADDRESS);

    const indexSet = 1n << BigInt(outcomeIndex);
    const collectionId = keccak256(
      encodePacked(
        ['bytes32', 'bytes32', 'uint256'],
        [zeroBytes32, conditionId as `0x${string}`, indexSet],
      ),
    );

    const calculatedPusdTokenId = BigInt(
      keccak256(encodePacked(['address', 'bytes32'], [checksummedPusd, collectionId])),
    ).toString();

    return BigInt(tokenId).toString() === calculatedPusdTokenId;
  } catch (error) {
    console.error('[isPusdCollateral] Error calculating token ID:', error);
    return true;
  }
}

/**
 * Detects errors that leave the outcome of an order *undetermined* rather than
 * proving it failed.
 *
 * The CLOB/relayer pipeline can throw AFTER the signed order has already been
 * accepted and matched on-chain — e.g. the relayer `.wait()` for an allowance
 * approval times out, or the HTTP ack for a matched order is lost to a network
 * blip / gateway 5xx. In those cases the position really does land (and shows up
 * on the next portfolio refresh), so telling the user "the trade did not go
 * through" is wrong and pushes them to re-submit and double-trade.
 *
 * A definitive rejection from the matching engine (insufficient balance, price
 * band, `ok:false`, etc.) does NOT match any of these signals and is still
 * surfaced as a hard error.
 */
function isIndeterminateOrderError(err: any): boolean {
  const msg = (err?.message || '').toLowerCase();
  const status = err?.status ?? err?.response?.status ?? err?.statusCode;
  const code = err?.code;

  if (
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('settle') || // relayer "Timed out waiting for transaction ... to settle"
    msg.includes('network error') ||
    msg.includes('socket hang up') ||
    msg.includes('econnaborted') ||
    msg.includes('econnreset')
  ) {
    return true;
  }

  if (code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ETIMEDOUT') {
    return true;
  }

  // Transient transport / gateway failures that can occur after the engine
  // has already processed the order.
  if (status === 408 || status === 502 || status === 503 || status === 504) {
    return true;
  }

  return false;
}

/**
 * useClobOrder
 *
 * Manages the lifecycle of a CLOB order, including placement, cancellation, and redemption.
 * Handles SDK V2 nuances like market buy fee adjustment and optimistic UI updates.
 */
export function useClobOrder() {
  const clobClient = useClobClient();
  const { user } = useAuth();
  const { client: polymarketRelayClient } = usePolymarketClient();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingIds, setSubmittingIds] = useState<string[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);

  const walletAddress = user?.proxyWalletAddress;

  /**
   * Optimistic cache updates after a successful trade.
   * This provides a "zero-latency" feel before the next query poll.
   */
  const updateCacheAfterTrade = useCallback(() => {
    // Invalidate and refetch immediately
    queryClient.invalidateQueries({ queryKey: PORTFOLIO_QUERY_KEYS.all });
    queryClient.invalidateQueries({ queryKey: ORDERS_QUERY_KEYS.all });
    queryClient.invalidateQueries({ queryKey: STREAK_QUERY_KEYS.all });

    // Secondary refetch after 1.5s (likely when balances reflect)
    setTimeout(() => {
      queryClient.refetchQueries({ queryKey: PORTFOLIO_QUERY_KEYS.all });
      queryClient.refetchQueries({ queryKey: ORDERS_QUERY_KEYS.all });
    }, 1500);

    // Tertiary refetch after 4s (likely when positions indexing is done)
    setTimeout(() => {
      queryClient.refetchQueries({ queryKey: PORTFOLIO_QUERY_KEYS.all });
      queryClient.refetchQueries({ queryKey: ORDERS_QUERY_KEYS.all });
    }, 4000);
  }, [queryClient]);

  /**
   * Places a new order (Limit or Market)
   */
  const submitOrder = useCallback(
    async (params: PlaceOrderParams) => {
      if (!clobClient || !walletAddress) {
        throw new Error('Trading client not ready. Please check your connection.');
      }

      setIsSubmitting(true);
      setError(null);
      setSubmittingIds((prev) => [...prev, params.tokenId]);

      try {
        const isMarket = !params.price;
        const side = params.side === 'BUY' ? OrderSide.BUY : OrderSide.SELL;
        const builderCode = formatBuilderCode(POLY_BUILDER_CODE) || undefined;

        // OrderResponse is a discriminated union:
        //   { ok: true, orderId, status } | { ok: false, code, message }
        let response;

        if (isMarket) {
          // Tick size, neg-risk and balance/allowance are now resolved automatically
          // by the SDK. A market BUY takes a USD `amount` (with an optional `maxSpend`
          // cap); a market SELL takes `shares`. FOK (fill-or-kill) preserves the
          // previous createAndPostMarketOrder behavior.
          if (side === OrderSide.BUY) {
            response = await clobClient.placeMarketOrder({
              tokenId: params.tokenId,
              side: OrderSide.BUY,
              amount: params.amount,
              maxSpend:
                params.userUSDCBalance && params.userUSDCBalance > 0
                  ? params.userUSDCBalance * 0.995
                  : undefined,
              orderType: OrderType.FOK,
              builderCode,
            });
          } else {
            response = await clobClient.placeMarketOrder({
              tokenId: params.tokenId,
              side: OrderSide.SELL,
              shares: params.amount,
              orderType: OrderType.FOK,
              builderCode,
            });
          }
        } else {
          // Limit order: GTC by default, GTD when an expiration (unix seconds) is set.
          response = await clobClient.placeLimitOrder({
            tokenId: params.tokenId,
            price: params.price as number,
            size: params.amount,
            side,
            expiration: params.expiration || undefined,
            builderCode,
          });
        }

        if (response.ok) {
          setOrderId(response.orderId);
          toast.success(MESSAGES.TOAST.ORDER_PLACED);
          updateCacheAfterTrade();
          return { success: true, orderId: response.orderId };
        } else {
          throw new Error(response.message || 'The matching engine rejected your order.');
        }
      } catch (err: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[useClobOrder] Submission Error:', err);
        }

        // The order may have actually landed even though the SDK/relayer threw
        // (lost ack, relayer settlement timeout, gateway blip). Never tell the
        // user it "failed" in that case — reconcile against the server and show
        // a neutral "confirming" message so they don't re-submit and double-trade.
        if (isIndeterminateOrderError(err)) {
          setError(err);
          updateCacheAfterTrade();
          toast.warning(MESSAGES.TOAST.ORDER_PENDING_CONFIRMATION);
          throw err;
        }

        const msg = err.message || 'Failed to submit order. Please try again.';
        setError(new Error(msg));
        toast.error(msg);
        throw err;
      } finally {
        setIsSubmitting(false);
        setSubmittingIds((prev) => prev.filter((id) => id !== params.tokenId));
      }
    },
    [clobClient, walletAddress, updateCacheAfterTrade],
  );

  /**
   * Cancels a specific resting order
   */
  const cancelOrder = useCallback(
    async (id: string) => {
      if (!clobClient) return;
      setSubmittingIds((prev) => [...prev, id]);
      try {
        await clobClient.cancelOrder({ orderId: id });
        toast.success(MESSAGES.TOAST.ORDER_CANCELLED);
        updateCacheAfterTrade();
      } catch (e: any) {
        toast.error(`Cancellation failed: ${e.message}`);
      } finally {
        setSubmittingIds((prev) => prev.filter((oid) => oid !== id));
      }
    },
    [clobClient, updateCacheAfterTrade],
  );

  /**
   * Redeems winning positions (gasless)
   *
   * @param conditionIds - The condition ID(s) to redeem.
   * @param options - Optional override for proxy address and kind.
   */
  const redeemPosition = useCallback(
    async (
      conditionIds:
        | string
        | string[]
        | {
            conditionId: string;
            outcome?: string;
            negRisk?: boolean;
            outcomeIndex?: number;
            tokenId?: string;
          }[],
      options?: {
        proxyAddress?: string;
        kind?: string;
        outcome?: string;
        negRisk?: boolean;
        outcomeIndex?: number;
        tokenId?: string;
      },
    ) => {
      if (!walletAddress || !polymarketRelayClient) {
        // toast.error('Trading connection lost. Reconnecting...');
        return;
      }

      let parsedItems: {
        conditionId: string;
        outcome?: string;
        negRisk?: boolean;
        outcomeIndex?: number;
        tokenId?: string;
      }[] = [];
      if (typeof conditionIds === 'string') {
        parsedItems = [
          {
            conditionId: conditionIds,
            outcome: options?.outcome,
            negRisk: options?.negRisk,
            outcomeIndex: options?.outcomeIndex,
            tokenId: options?.tokenId,
          },
        ];
      } else if (Array.isArray(conditionIds)) {
        parsedItems = conditionIds.map((item) => {
          if (typeof item === 'string') {
            return {
              conditionId: item,
              outcome: options?.outcome,
              negRisk: options?.negRisk,
              outcomeIndex: options?.outcomeIndex,
              tokenId: options?.tokenId,
            };
          }
          return {
            conditionId: item.conditionId,
            outcome: item.outcome || options?.outcome,
            negRisk: item.negRisk || options?.negRisk,
            outcomeIndex:
              item.outcomeIndex !== undefined ? item.outcomeIndex : options?.outcomeIndex,
            tokenId: item.tokenId || options?.tokenId,
          };
        });
      }

      const conditionIdStrings = parsedItems.map((item) => item.conditionId);
      setSubmittingIds((prev) => [...prev, ...conditionIdStrings]);

      try {
        // 1. Determine the correct execution address and wallet kind.
        // We prioritize explicit options (passed from position list) then fallback to Redux.
        const kind = (options?.kind || user?.tradingWalletKind || 'gnosis_safe') as any;
        const executionAddress = normalizeClobAddress(
          options?.proxyAddress || user?.proxyWalletAddress || walletAddress,
        );

        // 2. Prepare the batch transaction payload
        // We dynamically route to either V2 pUSD Adapter or Legacy USDC.e CTF Contract.
        // This ensures that we make exactly one relayer submit API call per position,
        // avoiding duplicate requests, nonce collisions, and 400 relay errors.
        const transactions: any[] = [];
        const checksummedCtf = getAddress(CTF_CONTRACT_ADDRESS);
        const checksummedAdapter = getAddress(CTF_COLLATERAL_ADAPTER_ADDRESS);
        const checksummedNegRiskAdapter = getAddress(NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS);
        const checksummedPusd = getAddress(PUSD_CONTRACT_ADDRESS);
        const checksummedUsdcE = getAddress(USDC_E_CONTRACT_ADDRESS);
        const zeroBytes32 =
          '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`;

        parsedItems.forEach(({ conditionId: id, outcome, negRisk, outcomeIndex, tokenId }) => {
          // Ensure conditionId is a valid 32-byte hex string
          let conditionId = id;
          if (isHex(conditionId) && conditionId.length < 66) {
            conditionId = pad(conditionId as `0x${string}`, { size: 32 });
          } else if (!conditionId.startsWith('0x')) {
            conditionId = `0x${conditionId}`;
            if (conditionId.length < 66) {
              conditionId = pad(conditionId as `0x${string}`, { size: 32 });
            }
          }

          // Determine indexSets based on the outcome
          let indexSets: bigint[] = [1n, 2n]; // Fallback to both YES and NO
          if (outcomeIndex !== undefined && outcomeIndex !== null) {
            indexSets = [1n << BigInt(outcomeIndex)];
          } else if (outcome) {
            const normalizedOutcome = outcome.toUpperCase();
            if (normalizedOutcome === 'YES') {
              indexSets = [1n];
            } else if (normalizedOutcome === 'NO') {
              indexSets = [2n];
            }
          }

          const baseAbi = [
            {
              name: 'redeemPositions',
              type: 'function',
              inputs: [
                { name: 'collateralToken', type: 'address' },
                { name: 'parentCollectionId', type: 'bytes32' },
                { name: 'conditionId', type: 'bytes32' },
                { name: 'indexSets', type: 'uint256[]' },
              ],
            },
          ];

          // Determine the V2 pUSD Collateral Adapter target based on negRisk flag
          const pusdAdapterAddress = negRisk ? checksummedNegRiskAdapter : checksummedAdapter;

          // Resolve outcome index for offline hashing
          let calculatedOutcomeIndex = outcomeIndex;
          if (calculatedOutcomeIndex === undefined || calculatedOutcomeIndex === null) {
            if (outcome) {
              const normalized = outcome.toUpperCase();
              if (normalized === 'YES') calculatedOutcomeIndex = 0;
              else if (normalized === 'NO') calculatedOutcomeIndex = 1;
            }
          }

          const usePusd =
            !!negRisk || isPusdCollateral(tokenId, conditionId, calculatedOutcomeIndex);

          if (usePusd) {
            // Call 1: New pUSD Collateral (sent to Collateral Adapter)
            transactions.push({
              to: pusdAdapterAddress,
              data: encodeFunctionData({
                abi: baseAbi,
                args: [checksummedPusd, zeroBytes32, conditionId as `0x${string}`, indexSets],
              }),
              value: '0',
            });
          } else if (!negRisk) {
            // Call 2: Legacy USDC.e Collateral (sent directly to CTF Contract)
            // We only include this for standard markets as Neg Risk markets are strictly V2 pUSD-native.
            // Including this call for a Neg Risk market will cause the Safe multi-send batch to revert.
            transactions.push({
              to: checksummedCtf,
              data: encodeFunctionData({
                abi: baseAbi,
                args: [checksummedUsdcE, zeroBytes32, conditionId as `0x${string}`, indexSets],
              }),
              value: '0',
            });
          }
        });

        // 3. Execute via the Polymarket Relayer
        const response = await executeRelayMetaTxBatch(
          polymarketRelayClient as any,
          executionAddress,
          kind,
          transactions.map((txn) => ({
            to: txn.to,
            data: txn.data,
            value: txn.value,
          })),
          `Redeem ${parsedItems.length} position(s)`,
        );

        // 4. Wait for on-chain confirmation
        if (response && typeof (response as any).wait === 'function') {
          // The SDK wait() returns the final transaction object or undefined on timeout/failure
          const result = await (response as any).wait();

          if (!result) {
            throw new Error(
              'Redemption transaction timed out or was not found by the relayer. It may still be pending.',
            );
          }

          if (result.state === 'STATE_FAILED' || result.state === 'STATE_INVALID') {
            throw new Error(
              `Transaction failed on-chain: ${result.state}. Hash: ${result.transactionHash || 'N/A'}`,
            );
          }
        }

        toast.success(MESSAGES.TOAST.PAYOUT_SUCCESSFUL);
        updateCacheAfterTrade();
      } catch (err: any) {
        console.error('[useClobOrder] Redemption failed:', err);
        const errorMsg = err.message || 'Unknown error';
        toast.error(`Redemption failed: ${errorMsg}`);
        // Re-throw so that the caller (e.g. auto-redeem loop) can handle it
        throw err;
      } finally {
        setSubmittingIds((prev) => prev.filter((i) => !conditionIdStrings.includes(i)));
      }
    },
    [
      walletAddress,
      polymarketRelayClient,
      user?.tradingWalletKind,
      user?.proxyWalletAddress,
      updateCacheAfterTrade,
    ],
  );

  return {
    submitOrder,
    cancelOrder,
    cancelAllOrders: useCallback(async () => {
      if (!clobClient) return;
      await clobClient.cancelAll();
      toast.success(MESSAGES.TOAST.ALL_OPEN_ORDERS_CANCELLED);
      updateCacheAfterTrade();
    }, [clobClient, updateCacheAfterTrade]),
    redeemPosition,
    isSubmitting,
    submittingIds,
    error,
    orderId,
  };
}
